import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QuoteWizard } from '../../components/quote/QuoteWizard';
import { quoteService } from '../../services/quoteService';
import type { Quote } from '../../types/quote';
import { ApiError } from '../../utils/errorHandling';

export function EditQuote() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load the quote data
  useEffect(() => {
    const loadQuote = async () => {
      if (!id) {
        setError('Quote ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await quoteService.getQuoteById(id);
        setQuote(response.quote);
      } catch (error) {
        console.error('Error loading quote:', error);
        setError(error instanceof ApiError ? error.message : 'Failed to load quote');
      } finally {
        setLoading(false);
      }
    };

    loadQuote();
  }, [id]);

  const handleQuoteUpdated = useCallback((updatedQuote: Quote) => {
    // Navigate back to quote detail page with success message
    navigate(`/admin/quotes/${updatedQuote.id}`, {
      state: { 
        updatedQuote,
        message: `Quote ${updatedQuote.quoteNumber} updated successfully!`
      }
    });
  }, [navigate]);

  const handleCancel = useCallback(() => {
    // Navigate back to quote detail page
    navigate(`/admin/quotes/${id}`);
  }, [navigate, id]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading quote data...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-900 mb-2">
              Failed to Load Quote
            </h3>
            <p className="text-red-800 mb-4">
              {error || 'Quote not found'}
            </p>
            <button
              onClick={() => navigate('/admin/quotes')}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Back to Quotes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the unified wizard in edit mode
  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <QuoteWizard
        mode="edit"
        existingQuote={quote}
        onComplete={handleQuoteUpdated}
        onCancel={handleCancel}
      />
    </div>
  );
} 